import { Component, effect, EventEmitter, Input, Output } from '@angular/core';
import { SessionProgressNoteModel } from '../../../core/api/models/session-progress-note-model';
import { StaffApiService, SupervisorApiService } from '../../../core/api/services';
import { AlertService } from '../../../shared/services/alert.service';
import { PatientBehaviorModel, PatientBehaviorType, PatientProgramModel, PatientSessionModel, PatientTargetDataModel, PatientTargetModel, TargetType, TreatmentGoalModel, PromptCodeModel, SkillAcquisitionGoalModel, BehaviorGoalModel } from '../../../core/api/models';
import { SupervisorHelperService } from '../../../supervisor/supervisor.helper.service';
import { PatientSessionService } from '../patient-session.service';
import { forkJoin, of } from 'rxjs';
import { OfflineDataService } from '../../../core/services/offline-data.service';
import { catchError, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-progress-note',
  templateUrl: './progress-note.component.html',
  standalone: false,
})
export class ProgressNoteComponent {
  @Input() PatientSessionID!: number;
  @Input() isPreview: boolean = false;
  @Input() targets: PatientTargetModel[] = [];
  @Input() behaviors: PatientBehaviorModel[] = [];
  @Input() programs: PatientProgramModel[] = [];
  @Input() patientSessionForPreview: PatientSessionModel | null = null;
  @Output() endSessionEvent = new EventEmitter<void>();
  possiblePresentAtSession: string[] = ['Parent/Guardian', 'BCBA', 'Siblings', 'Peer'];
  presentAtSession: string[] = [];
  othersPresentAtSession: string = '';
  progressNoteStep: number = 1;
  progressNoteData!: SessionProgressNoteModel;
  skillAcquisitionGoals: SkillAcquisitionGoalModel[] = [];
  behaviorGoals: BehaviorGoalModel[] = [];
  sessionInterventions: string[] = [];
  otherInterventionAtSession: string = '';
  patientSession: PatientSessionModel | null = null;
  linkedGoals: string[] = [];
  //bipInterventions: string[] = [];
  interventions: string[] = [
    "Antecedent Based Intervention"
    , "Behavior Skills Training(BST)"
    , "Backward Chaining"
    , "Forward Chaining"
    , "Reinforcement of Incompatible Behavior"
    , "Reinforcement of Alternative Behavior"
    , "Discrete Trial Procedures"
    , "Total Task Presentation"
    , "Token Reinforcement"
    , "Most-to-Least Prompting"
    , "Least-to-Most Prompting"
    , "Modeling"
    , "Self-management Techniques"
    , "Naturalistic Intervention"
    , "Video Modeling"
    , "Visual Supports"
  ];
  goalProgresses: any[] = [];
  isDataSummaryLoading = false;
  constructor(
    private staffApiService: StaffApiService,
    private supervisorApiService: SupervisorApiService,
    private patientSessionService: PatientSessionService,
    private offlineDataService: OfflineDataService
  ) {
    effect(() => {
      this.patientSessionService.dataChanged();
      this.patientSession = this.patientSessionService.getPatientSession();
      this.getData();
    });
  }

  getData() {
    const patientId = this.patientSession?.PatientID ?? 0;

    // When we have offline data, use it for programs and goals (works in both preview and session mode)
    if (this.offlineDataService.hasOfflinePatientData(patientId)) {
      const offlineData = this.offlineDataService.getOfflinePatientData(patientId);
      if (offlineData) {
        this.programs = offlineData.programs ?? this.programs;
        this.skillAcquisitionGoals = offlineData.skillAcquisitionGoals ?? this.skillAcquisitionGoals;
        this.behaviorGoals = offlineData.behaviorGoals ?? this.behaviorGoals;
        if (!this.isPreview) {
          const offlineProgressNote = this.offlineDataService.getOfflineSessionProgressNotes(patientId, this.PatientSessionID);
          if (offlineProgressNote) {
            this.progressNoteData = offlineProgressNote;
            this.getProgressNoteData();
            return;
          }
        }
      }
    }


    if (this.PatientSessionID >= 0) {
      // If we're offline but don't have the data, or if we're online, try to fetch and save for offline
      if (this.offlineDataService.isInOfflineMode()) {
        // In offline mode with no data - initialize with empty data
        this.progressNoteData = {
          PatientSessionID: this.PatientSessionID,
          PresentAtSession: '',
          Progress: '',
          DataSummary: '',
          ProgressNoteGoalProgress: [],
          Interventions: []
        };
        this.getProgressNoteData();
      } else {
        // Online mode - fetch from server and save for offline
        this.isDataSummaryLoading = true;
        const getSkillAcquisitionsObservable = this.supervisorApiService.GetPatientSkillAcquisitionGoals({
          patientID: patientId
        });
        const getBehaviorGoalsObservable = this.supervisorApiService.GetPatientBehaviorGoals({
          patientID: patientId
        });

        // Use our offline service to get and save progress notes
        const getProgressNoteObservable = this.offlineDataService.saveOfflineSessionProgressNotes(patientId, this.PatientSessionID);

        // Programs come from parent (@Input) or from offline data - no need to fetch again
        forkJoin([getSkillAcquisitionsObservable, getProgressNoteObservable, getBehaviorGoalsObservable]).subscribe(([skillAcquisitionGoals, progressNote, behaviorGoals]) => {
          this.skillAcquisitionGoals = skillAcquisitionGoals ?? [];
          this.behaviorGoals = behaviorGoals ?? [];
          this.progressNoteData = progressNote || {
            PatientSessionID: this.PatientSessionID,
            PresentAtSession: '',
            Progress: '',
            DataSummary: '',
            ProgressNoteGoalProgress: [],
            Interventions: []
          };
          this.getProgressNoteData();
          this.isDataSummaryLoading = false;
        }, () => {
          this.isDataSummaryLoading = false;
        });
      }
    }
    else {
      this.progressNoteData = {
        PatientSessionID: this.PatientSessionID,
        PresentAtSession: '',
        Progress: '',
        DataSummary: '',
        ProgressNoteGoalProgress: [],
        Interventions: []
      };
      if (this.isPreview) {
        this.progressNoteData.DataSummary = this.calculateProgressNotesDataSummary(this.patientSessionForPreview);
      }
      else {
        this.progressNoteData.DataSummary = this.calculateProgressNotesDataSummary(this.patientSession);
      }
    }

  }

  getProgressNoteData(progressNote?: SessionProgressNoteModel) {
    this.progressNoteData = progressNote ?? this.progressNoteData;
    if (!this.progressNoteData) {
      this.progressNoteData = {
        PatientSessionID: this.PatientSessionID,
        PresentAtSession: '',
        Progress: '',
        DataSummary: '',
        ProgressNoteGoalProgress: [],
        Interventions: []
      };
    }

    const allPresentAtSession = this.progressNoteData.PresentAtSession?.split(', ') || [];
    this.presentAtSession = allPresentAtSession.filter((item) => this.possiblePresentAtSession.includes(item));
    this.othersPresentAtSession = allPresentAtSession.filter((item) => !this.possiblePresentAtSession.includes(item)).join(', ') || '';
    this.sessionInterventions = this.progressNoteData.Interventions || [];

    /*if (this.bipInterventions) {
      const otherIntervention = this.sessionInterventions.find(f => this.bipInterventions.indexOf(f) < 0);
      if (otherIntervention)
        this.otherInterventionAtSession = otherIntervention;
    }*/
    if (this.interventions) {
      const otherIntervention = this.sessionInterventions.find(f => this.interventions.indexOf(f) < 0);
      if (otherIntervention)
        this.otherInterventionAtSession = otherIntervention;
    }
    //get goals that are linked to the session
    this.getLinkedGoals();
    //this.getBIPInterventions();


    if (this.isPreview) {
      this.progressNoteData.DataSummary = this.calculateProgressNotesDataSummary(this.patientSessionForPreview);
    }
    else {
      this.progressNoteData.DataSummary = this.calculateProgressNotesDataSummary(this.patientSession);
    }

  }


  calculateProgressNotesDataSummary(patientSession: PatientSessionModel | null): string {
    if (!patientSession) {
      return '';
    }

    const targetData = patientSession.TargetData ?? [];
    const behaviorData = patientSession.BehaviorData ?? [];

    //get distinct programs from the targetData (check also subtargets); a target can belong to multiple programs
    const distinctPrograms = targetData.filter(x => !x.DeletedDate)
      .reduce((acc: any[], x: PatientTargetDataModel) =>
        acc.concat(this.programs.filter(p => p.Targets?.some(t => t.ID === x.PatientTargetID || t.SubTargets?.some(st => st.ID === x.PatientTargetID)))), [])
      .filter((value: any, index: number, self: any[]) =>
        index === self.findIndex((t: any) => t && value && t.ID === value.ID))
      .filter((p: any) => p != null) || [];

    //get distinct targets from the targetData
    const distinctTargets = targetData.filter((x) => x.PatientTargetID && !x.DeletedDate).map((x) => this.targets.find((y) => y.ID === x.PatientTargetID))
      .filter((value: any, index: number, self: any[]) =>
        index === self.findIndex((t: any) => t && value && t.ID === value.ID)) ?? [];

    const appendTargetSummary = (target: PatientTargetModel, targetData: any[], output: string) => {
      const fatherTarget = this.targets.find((x) => x.SubTargets?.some((y) => y.ID === target.ID));
      var targetName = target.TargetName;
      if (fatherTarget) {
        targetName = `${fatherTarget.TargetName} - ${targetName}`;
      }

      if (target.TargetType === TargetType.Frequency) {
        const value = targetData.filter((x: any) => x.IntValue > 0).length;
        return output + `<li>"${targetName}" - occurred ${value} times</li>`;
      }
      if (target.TargetType === TargetType.Duration) {
        const value = targetData.reduce((sum: number, x: any) => sum + (x.IntValue || 0), 0);
        const minutes = Math.floor(value / 60);
        const seconds = value % 60;
        const count = targetData.length;
        const formattedMinutes = minutes.toString().padStart(2, '0');
        const formattedSeconds = seconds.toString().padStart(2, '0');
        return output + `<li>"${targetName}" - occurred ${count} times with a total duration of ${formattedMinutes} min ${formattedSeconds} sec</li>`;
      }
      if (target.TargetType === TargetType.Anecdotal) {
        const count = targetData.length;
        let result = output + `<li>"${targetName}" - anecdotals recorded ${count} times:<ul>`;
        targetData.forEach((targetDataItem: any) => {
          result += `<li>${targetDataItem.TextValue}</li>`;
        });
        return result + "</ul></li>";
      }
      if (target.TargetType === TargetType.DiscreteTrialTeaching || target.TargetType === TargetType.Echoic) {
        const count = targetData.length;
        if (count > 0) {
          const promptCodes = target.AllPromptCodes ?? [];
          if (promptCodes && promptCodes.length > 0) {
            const promptCodesDictionary = promptCodes.reduce((dict: any, x: any) => { dict[x.ID] = x; return dict; }, {});
            const countIndependent = targetData.filter((x: any) => promptCodesDictionary[x.IntValue] && promptCodesDictionary[x.IntValue].IsIndependent).length;
            const percentageIndependent = Math.round(countIndependent * 100 / count);
            return output + `<li>"${targetName}" - ${percentageIndependent}% success (total ${count} trials)</li>`;
          }
        }
      }
      if (target.TargetType === TargetType.FluencyRate) {
        const count = targetData.length;
        let result = output + `<li>"${targetName}" - recorded ${count} times:<ul>`;
        targetData.forEach((targetDataItem: any) => {
          const values = targetDataItem.TextValue.split('/');
          if (values.length === 2) {
            const valuesInt = values.map((x: string) => parseInt(x, 10));
            const rate = (valuesInt[1] + valuesInt[0] === 0) ? 0 : Math.round((valuesInt[0] / (valuesInt[1] + valuesInt[0])) * 100);
            result += `<li>${rate}% correct - ${values[0]} correct / ${values[1]} incorrect</li>`;
          }
        });
        return result + "</ul></li>";
      }
      if (target.TargetType === TargetType.Quantity) {
        const count = targetData.length;
        const value = targetData.reduce((sum: number, x: any) => sum + (x.IntValue || 0), 0);
        return output + `<li>"${targetName}" - occurred ${count} times with a total of ${value}</li>`;
      }
      if (target.TargetType === TargetType.Intensity) {
        const ratings = targetData
          .filter((x: any) => x.IntValue != null)
          .sort((a: any, b: any) => new Date(a.RecordedDate).getTime() - new Date(b.RecordedDate).getTime())
          .map((x: any) => x.IntValue as number);
        const count = ratings.length;
        const lastRating = count > 0 ? ratings[ratings.length - 1] : null;
        const average = count > 0 ? (ratings.reduce((sum: number, value: number) => sum + value, 0) / count) : 0;
        return output + `<li>"${targetName}" - recorded ${count} intensity rating${count === 1 ? '' : 's'}` +
          (count > 0 ? ` (last ${lastRating}, average ${average.toFixed(1)})` : '') +
          `</li>`;
      }
      if (target.TargetType === TargetType.WholePartialInterval) {
        const count = targetData.length;
        const value = targetData.reduce((sum: number, x: any) => sum + (x.IntValue || 0), 0);
        return output + `<li>"${targetName}" - during ${count} intervals it happened ${value} times</li>`;
      }
      if (target.TargetType === TargetType.Toileting) {
        const count = targetData.length;
        const urinationOn = targetData.reduce((sum: number, x: any) => sum + ((x.ToiletingData?.UrinationOn ?? false) ? 1 : 0), 0);
        const urinationOff = targetData.reduce((sum: number, x: any) => sum + ((x.ToiletingData?.UrinationOff ?? false) ? 1 : 0), 0);
        const bowelMovementOn = targetData.reduce((sum: number, x: any) => sum + ((x.ToiletingData?.BowelMovementOn ?? false) ? 1 : 0), 0);
        const bowelMovementOff = targetData.reduce((sum: number, x: any) => sum + ((x.ToiletingData?.BowelMovementOff ?? false) ? 1 : 0), 0);
        const noVoids = targetData.reduce((sum: number, x: any) => sum + ((x.ToiletingData?.NoVoid ?? false) ? 1 : 0), 0);
        if (urinationOn == 0 && urinationOff == 0 && bowelMovementOn == 0 && bowelMovementOff == 0 && noVoids == 0) {
          return output + `<li>"${targetName}" - no toileting events recorded</li>`;
        }
        const metricFragments: string[] = [];
        if (urinationOn > 0) metricFragments.push(`${urinationOn} times of urination on`);
        if (urinationOff > 0) metricFragments.push(`${urinationOff} times of urination off`);
        if (bowelMovementOn > 0) metricFragments.push(`${bowelMovementOn} times of bowel movement on`);
        if (bowelMovementOff > 0) metricFragments.push(`${bowelMovementOff} times of bowel movement off`);
        if (noVoids > 0) metricFragments.push(`${noVoids} times of no voids`);
        return output + `<li>"${targetName}" - during ${count} times it happened ` + metricFragments.join(', ') + '</li>';
      }
      return output;
    };

    // Targets not under any program
    const targetsWithoutProgram = distinctTargets.filter((target: PatientTargetModel | undefined) => {
      if (!target) return false;
      const fatherTarget = this.targets.find((x) => x.SubTargets?.some((y) => y.ID === target.ID));
      const hasProgram = (target.PatientProgramIDs?.length ?? 0) > 0 || (fatherTarget?.PatientProgramIDs?.length ?? 0) > 0;
      return !hasProgram;
    });

    // Skill Acquisition Programs section - only show when we have content
    const hasSkillAcquisitionContent = distinctPrograms.length > 0 || targetsWithoutProgram.length > 0;
    let dataSummary = '';

    if (hasSkillAcquisitionContent) {
      dataSummary = `Skill Acquisition Programs:`;
      dataSummary += "<ul>";

      // Programs section: targets grouped by program
      distinctPrograms.forEach((program: any) => {
        dataSummary += `<li>"${program.ProgramName}"`;
        var programTargetsSummary = "<ul>";

        distinctTargets.forEach((target: PatientTargetModel | undefined) => {
          if (!target) return;
          const targetDataItems = targetData.filter((x) => x.PatientTargetID === target.ID && !x.DeletedDate);
        const fatherTarget = this.targets.find((x) => x.SubTargets?.some((y) => y.ID === target.ID));

        if (!(target.PatientProgramIDs?.includes(program.ID) ?? false) &&
            (fatherTarget == null || !(fatherTarget.PatientProgramIDs?.includes(program.ID) ?? false))) {
          return;
        }

        programTargetsSummary = appendTargetSummary(target, targetDataItems, programTargetsSummary);
      });
      dataSummary += `${programTargetsSummary}</ul></li>`;
    });

      dataSummary += "</ul>";

      if (targetsWithoutProgram.length > 0) {
        dataSummary += `<br />Targets not under any program:`;
        dataSummary += "<ul>";

        targetsWithoutProgram.forEach((target: PatientTargetModel | undefined) => {
          if (!target) return;
          const targetDataItems = targetData.filter((x) => x.PatientTargetID === target.ID && !x.DeletedDate);
          dataSummary = appendTargetSummary(target, targetDataItems, dataSummary);
        });
        dataSummary += "</ul>";
      }
    }

    // Behavior Reduction section - only show when we have content
    const distinctBehaviors = behaviorData.filter((x) => x.PatientBehaviorID && !x.DeletedDate).map((x) => this.behaviors.find((y) => y.ID === x.PatientBehaviorID))
      .filter((value: any, index: number, self: any[]) =>
        index === self.findIndex((t: any) => t && value && t.ID === value.ID)) ?? [];

    if (distinctBehaviors.length > 0) {
      if (dataSummary) dataSummary += '<br />';
      dataSummary += `Behavior Reduction data:`;
      dataSummary += "<ul>";

      distinctBehaviors.forEach((behavior: PatientBehaviorModel | undefined) => {
        if (!behavior) return;
        const behaviorDataItems = behaviorData.filter((x) => x.PatientBehaviorID === behavior.ID && !x.DeletedDate);

        if (behavior.BehaviorType === PatientBehaviorType.Frequency) {
          const value = behaviorDataItems.filter((x: any) => x.IntValue > 0).length;
          dataSummary += `<li>"${behavior.PatientBehaviorName}" - occurred ${value} times</li>`;
        }
        if (behavior.BehaviorType === PatientBehaviorType.Duration) {
          const value = behaviorDataItems.reduce((sum: number, x: any) => sum + (x.IntValue || 0), 0);
          const minutes = Math.floor(value / 60);
          const seconds = value % 60;
          const count = behaviorDataItems.length;
          const formattedMinutes = minutes.toString().padStart(2, '0');
          const formattedSeconds = seconds.toString().padStart(2, '0');
          dataSummary += `<li>"${behavior.PatientBehaviorName}" - occurred ${count} times with a total duration of ${formattedMinutes} min ${formattedSeconds} sec</li>`;
        }
        if (behavior.BehaviorType === PatientBehaviorType.Interval) {
          const count = behaviorDataItems.length;
          const value = behaviorDataItems.reduce((sum: number, x: any) => sum + (x.IntValue || 0), 0);
          dataSummary += `<li>"${behavior.PatientBehaviorName}" - during ${count} intervals it happened ${value} times</li>`;
        }
        if (behavior.BehaviorType === PatientBehaviorType.Intensity) {
          const ratings = behaviorDataItems
            .filter((x: any) => x.IntValue != null)
            .sort((a: any, b: any) => new Date(a.RecordedDate).getTime() - new Date(b.RecordedDate).getTime())
            .map((x: any) => x.IntValue as number);
          const count = ratings.length;
          const lastRating = count > 0 ? ratings[ratings.length - 1] : null;
          const average = count > 0 ? (ratings.reduce((sum: number, value: number) => sum + value, 0) / count) : 0;
          dataSummary += `<li>"${behavior.PatientBehaviorName}" - recorded ${count} intensity rating${count === 1 ? '' : 's'}` +
            (count > 0 ? ` (last ${lastRating}, average ${average.toFixed(1)})` : '') +
            `</li>`;
        }
      });
      dataSummary += "</ul>";
    }

    return dataSummary;
  }

  getLinkedGoals() {
    //get distinct goals linked to the session on this.patientSession?.TargetData
    const targetIDsOnSession = this.patientSession?.TargetData?.map(item => item.PatientTargetID).filter(item => !!item) as number[] ?? [];
    const targetsOnSession = this.patientSessionService.getPatientTargets().filter(target => !!target && targetIDsOnSession.includes(target.ID ?? 0)) ?? [];
    const programsOnSession = this.patientSessionService.getPrograms().filter(program => !!program && program.Targets?.some(target => targetIDsOnSession.includes(target.ID ?? 0))) ?? [];

    const behaviorIDsOnSession = this.patientSession?.BehaviorData?.map(m => m.PatientBehaviorID).filter(item => !!item) as number[] ?? [];
    const behaviorsOnSession = this.patientSessionService.getPatientBehaviors().filter(g => !!g && behaviorIDsOnSession.includes(g.ID ?? 0)) ?? [];

    var skillAcquisitionGoals = this.skillAcquisitionGoals.filter(goal => targetsOnSession.find(t => t.BSP_SkillAcquisitionGoalID == goal.ID)
      || programsOnSession.find(p => p.BSP_SkillAcquisitionGoalID == goal.ID));

    var behaviorGoals = this.behaviorGoals.filter(goal => behaviorsOnSession.find(f => f.BSP_BehaviorGoalID == goal.ID));

    //add to goalProgresses the values from this.progressNoteData.ProgressNoteGoalProgress
    this.goalProgresses = skillAcquisitionGoals.map(goal => ({
      BSP_TreatmentGoalID: goal.ID ?? null,
      BSP_BehaviorGoalID: <number|null>null,
      Progress: ''
    })).concat(behaviorGoals.map(goal => ({
      BSP_TreatmentGoalID: <number|null>null,
      BSP_BehaviorGoalID: goal.ID ?? null,
      Progress: ''
    })));

    this.linkedGoals = skillAcquisitionGoals.map(goal => goal.Goal?? "").concat(behaviorGoals.map(goal => goal.Goal ?? ""));
  }


  /*
  getBIPInterventions() {
    //get distinct goals linked to the session on this.patientSession?.TargetData
    this.bipInterventions = this.patientSessionService.getBIPInterventions();
    if (this.bipInterventions && this.sessionInterventions) {
      const otherIntervention = this.sessionInterventions.find(f => this.bipInterventions.indexOf(f) < 0);
      if (otherIntervention)
        this.otherInterventionAtSession = otherIntervention;
      else {
        this.otherInterventionAtSession = "";
      }
    }
  }
  */

  backProgressNote() {
    if (this.progressNoteStep > 1) {
      this.progressNoteStep--;
      this.saveProgressNote();
    }
  }

  nextProgressNote() {
    this.progressNoteStep++;
    this.saveProgressNote();
  }

  changeData(event: any) {
    this.saveProgressNote();
  }

  saveProgressNote() {
    if (this.isPreview) {
      return;
    }

    const patientId = this.patientSession?.PatientID ?? 0;
    const allPresentAtSession = [...this.presentAtSession, this.othersPresentAtSession];
    this.progressNoteData.PresentAtSession = allPresentAtSession.filter(item => !!item).join(', ');
    this.progressNoteData.ProgressNoteGoalProgress = this.goalProgresses.map(goal => ({
      BSP_TreatmentGoalID: goal.BSP_TreatmentGoalID,
      BSP_BehaviorGoalID: goal.BSP_BehaviorGoalID,
      Progress: goal.Progress,
      PatientSessionID: this.PatientSessionID
    }));

    this.progressNoteData.Interventions = this.sessionInterventions;
    if (this.otherInterventionAtSession) {
      this.progressNoteData.Interventions.push(this.otherInterventionAtSession);
    }

    // Use offline data service to save progress notes
    this.offlineDataService.saveModifiedProgressNotes(patientId, this.PatientSessionID, this.progressNoteData)
      .subscribe(() => {
        console.log('Progress note saved successfully!');
      });
  }

  endSession() {
    if (!this.isPreview) {
      this.saveProgressNote();
    }
    this.endSessionEvent.emit();
  }
} 
